import React, { useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/utils';
import { useTheme } from '../../contexts/ThemeContext';

const AnimatedCard = React.forwardRef(({ className, children, delay = 0, ...props }, ref) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const cardRef = useRef(null);
  const { isDarkMode, getThemeClasses } = useTheme();
  const themeClasses = getThemeClasses();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setTimeout(() => {
            setIsVisible(true);
            setHasAnimated(true);
          }, delay);
        }
      },
      {
        threshold: 0.1, // Trigger when 10% visible
        rootMargin: '150px 0px -100px 0px' // Start animation earlier, more graceful
      }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, [delay, hasAnimated]);

  return (
    <div
      ref={cardRef}
      className={cn(
        "rounded-xl transform group cursor-pointer",
        "will-change-transform transition-all duration-700 ease-[cubic-bezier(0.34,1.56,0.64,1)]",
        isVisible 
          ? "opacity-100 translate-y-0 scale-100" 
          : "opacity-0 translate-y-8 scale-95",
        // Vision UI inspired card styling
        themeClasses.card,
        themeClasses.cardHover,
        "active:scale-[0.98]",
        className
      )}
      {...props}
    >
      <div className="relative overflow-hidden rounded-xl">
        {/* Subtle gradient overlay for depth - much more refined */}
        <div className={cn(
          "absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-xl",
          isDarkMode 
            ? "bg-gradient-to-br from-purple-500/5 via-transparent to-blue-500/5" 
            : "bg-gradient-to-br from-purple-500/3 via-transparent to-blue-500/3"
        )}></div>
        
        {/* Subtle shine effect - much more refined than before */}
        <div className={cn(
          "absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out rounded-xl",
          isDarkMode 
            ? "bg-gradient-to-r from-transparent via-white/5 to-transparent" 
            : "bg-gradient-to-r from-transparent via-white/8 to-transparent"
        )}></div>
        
        {children}
      </div>
    </div>
  );
});

AnimatedCard.displayName = "AnimatedCard";

const CardHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6 relative z-10", className)}
    {...props}
  />
));
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef(({ className, ...props }, ref) => {
  const { getThemeClasses } = useTheme();
  const themeClasses = getThemeClasses();
  
  return (
    <h3
      ref={ref}
      className={cn(
        "text-2xl font-semibold leading-none tracking-tight transition-colors duration-300",
        themeClasses.text.primary,
        className
      )}
      {...props}
    />
  );
});
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef(({ className, ...props }, ref) => {
  const { getThemeClasses } = useTheme();
  const themeClasses = getThemeClasses();
  
  return (
    <p
      ref={ref}
      className={cn(
        "text-sm transition-colors duration-300",
        themeClasses.text.secondary,
        className
      )}
      {...props}
    />
  );
});
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef(({ className, ...props }, ref) => (
  <div 
    ref={ref} 
    className={cn("p-6 pt-0 relative z-10", className)} 
    {...props} 
  />
));
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0 relative z-10", className)}
    {...props}
  />
));
CardFooter.displayName = "CardFooter";

export { AnimatedCard, CardHeader, CardTitle, CardDescription, CardContent, CardFooter };